import {
    AddressPriority,
    AgentRole,
    BankAccountType,
    BeneficiaryDesignation,
    ConvenientTime,
    CoverageType,
    Gender,
    InsuredRole,
    LivesType,
    PartyType,
    PaymentFrequency,
    PaymentType,
    PhonePriority,
    PhoneType,
    RatingType,
    RatingValueType,
    Relationship,
    ServiceType,
    SmokerStatus,
    Title,
    UnderwritingClass,
} from "@app/modules/policySpace/common/policyConstants";
import { ExtendedProperties, ExtendedProperty } from "@app/models/models";
import { CountryCode } from "@app/core/utilities/phones";
import { LozengeColor } from "@dit/ui-components.react.element.lozenge";

export interface DetailedPolicyResponse {
    policy: DetailedPolicy;
    parties: DetailedPolicyParty[];
}

//v3
export interface AssociatedParty {
    partyName: string;
}

//v3
export interface PaymentInformation {
    name: string;
    value: string;
}

export interface DetailedPolicy {
    productType: string;
    policyNumber: string;
    applicationNumber: string;
    hasApplication?: boolean;
    status: string;
    isIssued: boolean;
    isRefused: boolean;
    isTerminated: boolean;
    ownershipStructure: string;
    submissionDate: string;
    issueDate: string;
    effectiveDate: string;
    terminationDate: string;
    paymentDate: string;
    dueDate: string;
    yearlyPremium: number;
    modalPremium: number;
    inGraceStatus: boolean;
    enteredGraceDate: string;
    totalBaseCoverageFaceAmount: number;
    totalPrimaryInsuredFaceAmount: number;
    totalGracePremiumDue: number;
    productGroup: string;
    assetsAndValues: AssetsAndValues;
    coverages: DetailedPolicyCoverage[];
    owners: DetailedPolicyOwner[];
    agents: DetailedPolicyAgent[];
    payers: DetailedPolicyPayer[];
    disbursement: DetailedPolicyDisbursement;
    paymentDetails: DetailedPolicyPaymentDetails;
    //only v2
    associatedParties: DetailedPolicyAssociatedParties;
    notes: DetailedPolicyNote;
    currencyCode: string;
    //v3
    creditors: AssociatedParty[];
    //v3
    curators: AssociatedParty[];
    //v3
    executors: AssociatedParty[];
}

export interface AssetsAndValues {
    //Policy Values
    baseCashValue?: number;
    additionalCoverageCashValue?: number;
    dividendAccumulationValue?: number;
    loanPayoff?: number;
    suspenseUnappliedCash?: number;
    premiumPaidInAdvance?: number;
    surrenderValue?: number;
    costBasis: number;
    mec: string;
    reducedPaidUpFaceAmount?: number;
    etiFaceAmount?: number;

    //Dividend
    dividendOption?: string;
    anniversaryTotalDividendAmount?: number;
    policyTotalDividendAmount?: number;
    paidUpAdditionsPurchased?: number;
    dividendAccumulationRate?: number;

    //Loan Information
    loanPrincipal?: number;
    loanInterest?: number;
    aplPrincipal?: number;
    aplInterest?: number;
    loanInterestRate?: number;
}

export interface DetailedPolicyCoverage extends ExtendedProperties {
    coverageID: string;
    coverageType: CoverageType;
    coverageStatus: string;
    isIssued: boolean;
    isRefused: boolean;
    isTerminated: boolean;
    livesType: LivesType;
    productCode: string;
    productShortName: string;
    coverageName: string;
    coverageTerm: number;
    faceAmount: number;
    yearlyPremium: number;
    modalPremium: number;
    issueDate: string;
    premiumMaturityDate: string;
    maturityDate: string;
    renewalDate: string;
    jointIssueAge: number;
    jointGender: Gender;
    jointSmokerStatus: SmokerStatus;
    insureds: DetailedPolicyInsured[];
    agents: DetailedPolicyAgent[];
    statusChangeDate: string;
    waitingPeriod: number;
    benefitExpiry: string;
    premiumType: string;
    commissionSyndicate: string;
    nextRenewalAmount: number;
}

export interface DetailedPolicyInsured {
    partyID: string;
    issueAge: number;
    smokerStatus: SmokerStatus;
    occupation: string;
    insuredRole: InsuredRole;
    underwritingClass: UnderwritingClass;
    ratings: DetailedPolicyRating[];
    beneficiaries: DetailedPolicyBeneficiary[];
}

export interface DetailedPolicyRating {
    ratingType: RatingType;
    valueType: RatingValueType;
    value: number;
    dueDate: string;
}

export interface DetailedPolicyBeneficiary {
    partyID: string;
    coveragePercentage: number;
    designation: BeneficiaryDesignation;
    relationship: Relationship;
    //only v2
    associatedParties: DetailedPolicyAssociatedParties;
    //v3
    subrogatedParties: AssociatedParty[];
}

export interface DetailedPolicyOwner {
    partyID: string;
    ownershipPercentage: number;
    relationship: Relationship;
    //not used
    //associatedParties: DetailedPolicyAssociattedParties;
}

export interface DetailedPolicyAgent {
    partyID: string;
    agentRole: AgentRole;
    sharePercentage: number;
    authorityFlag: boolean;
}

export interface DetailedPolicyPayer {
    partyID: string;
}

export interface DetailedPolicyPaymentDetails {
    paymentType: PaymentType;
    paymentFrequency: PaymentFrequency;
    effectiveDay: number;
    //only v2
    bankAccount: DetailedPolicyBankAccount;
    //v3
    accountNumber: string;
    //v3
    paymentInformations: PaymentInformation[];
}

export interface DetailedPolicyDisbursement {
    nextDisbursementDate: string;
    paymentFrequency: PaymentFrequency;
    paymentType: string;
    payoutOption: string;
}
//v2
export interface DetailedPolicyBankAccount {
    bankNumber: string;
    bankName: string;
    transitNumber: string;
    branchAddress: string;
    accountType: BankAccountType;
    serviceType: ServiceType;
    routingNumber: string;
    accountNumber: string;
}

export interface DetailedPolicyParty {
    partyID: string;
    partyType: PartyType;
    title: Title;
    firstName: string;
    lastName: string;
    companyName: string;
    abn: string;
    acn: string;
    dateOfBirth: string;
    gender: Gender;
    email: string;
    addresses: DetailedPolicyAddress[];
    phones: DetailedPolicyPhone[];
}

export interface DetailedPolicyAddress {
    addressID: string;
    prioritySequence: AddressPriority;
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    address5: string;
    address6: string;
    correspondence: boolean;
    city: string;
    state: string;
    country: string;
    zipCode: string;
}

export interface DetailedPolicyPhone {
    phoneID: string;
    prioritySequence: PhonePriority;
    phoneType: PhoneType;
    phoneNumber: string;
    extension: string;
    convenientTime: ConvenientTime;
    countryCode: CountryCode;
}

export interface DetailedPolicyAssociatedParties {
    executors: DetailedPolicyExecutor[];
    creditors: DetailedPolicyCreditor[];
    curators: DetailedPolicyCurator[];
    trustees: DetailedPolicyTrustee[];
    subrogatedParties: DetailedPolicySubrogatedParty[];
}

export interface DetailedPolicyCreditor {
    partyID: string;
}

export interface DetailedPolicyCurator {
    partyID: string;
}

export interface DetailedPolicyTrustee {
    partyID: string;
}

export interface DetailedPolicyExecutor {
    partyID: string;
}

export interface DetailedPolicySubrogatedParty {
    partyID: string;
}

export interface DetailedPolicyNote {
    exclusions: string;
    additionalConsiderations: string;
}

export interface ContractAdditionalInformation extends ExtendedProperties {
    extendedProperties: ExtendedProperty[];
}

export interface InsuredViewModel {
    partyID: string;
    firstName: string;
    lastName: string;
    gender: Gender;
    dateOfBirth: string;
    smokerStatus: SmokerStatus;
    occupation: string;
    insuredRole: InsuredRole;
    issueAge: number;
    underwritingClass: UnderwritingClass;
    isOwner: boolean;
    isPayer: boolean;
    abn?: string;
    acn?: string;
}

export type PolicyStatusFlags = {
    isIssued: boolean;
    isRefused: boolean;
    isTerminated: boolean;
};

export interface AvailableChangeRequestsResponse {
    availableChangeRequests: string[];
}

export interface AvailableClaimsResponse {
    availableClaims: string[];
}

export interface LozengeStatusStyle {
    lozengeColor: LozengeColor;
}

export interface StatusStyles {
    iconColor: string;
    textColor: string;
}

export const defaultPolicyDetailConfig: PolicyDetailConfigurations = {
    actionButtons: {
        agent: ["changeRequest", "claim", "uploadDocument", "policyDocument", "requestEpoQuote", "test"],
        client: ["changeRequest", "claim", "uploadDocument", "policyDocument", "requestEpoQuote", "test"],
    },
};
